'use client';

import { useId, useState } from 'react';

import { cn } from '../cn';

import { ArrowIcon } from './ArrowIcon';
import { Button } from './Button';
import { Input } from './Input';

type Status = 'error' | 'idle' | 'sending' | 'sent';

/* The button carries the hue through the recipe's `tone`; the field's edge is
   the 300 step of the same hue, or the Input's own slate. */
const TONE = {
  periwinkle: { input: '', button: 'periwinkle' },
  scarlet: { input: 'border-scarlet-300', button: 'scarlet' },
} as const;

/**
 * Subscribes to the Mailjet newsletter list through the app's own route: the marketing
 * site and the blog each serve /api/newsletter/subscribe, so the form posts to
 * the same path wherever it renders.
 */
type NewsletterSignupProps = {
  className?: string;
  label?: string;
  /** A type step for the label. The steps are registered with tailwind-merge,
      so one passed here replaces the default rather than fighting it. */
  labelClassName?: string;
  /** The form's hue: the button's and the field's edge. Periwinkle unless told otherwise. */
  tone?: keyof typeof TONE;
  /**
   * A 40px field and button, and no line held open under them for the
   * outcome. The footer keeps that line so it does not shift on submit. The
   * marketing popup and the blog's hero card do not: a response grows the card rather than
   * leaving an empty line that read as 28px more padding below than above.
   */
  compact?: boolean;
  /** Fired once the confirmation request is accepted. */
  onSuccess?: () => void;
};

export const NewsletterSignup = ({
  className,
  label = 'Get the occasional update',
  labelClassName,
  tone = 'periwinkle',
  compact = false,
  onSuccess,
}: NewsletterSignupProps) => {
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = (await response.json()) as { error?: string; message?: string };

      if (response.ok) {
        setStatus('sent');
        setMessage(body.message ?? "Thanks. You're on the list.");
        setEmail('');
        onSuccess?.();
      } else {
        setStatus('error');
        setMessage(body.error ?? 'Could not sign you up. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Could not sign you up. Please try again.');
    }
  };

  return (
    <form onSubmit={submit} className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={inputId} className={cn('label text-slate-600', labelClassName)}>
        {label}
      </label>

      {/* The field and the button meet with no gap, so the button takes
          float={false} or its hover lift tears the seam between them. */}
      <div className='flex -translate-x-0.5 translate-y-0.5'>
        {/* h-auto, so the field stretches to the row, which is the button's
            height at every step. The Input's own h-9 / lg:h-12 and the
            button's h-11 / lg:h-12 agree nowhere under lg, and the button
            hung 8px under the field on a phone. */}
        <Input
          id={inputId}
          type='email'
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
          placeholder='Email address'
          autoComplete='email'
          disabled={status === 'sending'}
          invalid={status === 'error'}
          className={cn(
            'h-auto flex-1 rounded-r-none border-r-0 lg:h-auto',
            /* 16px on a phone: iOS zooms the page into any field set smaller,
               and the compact step is 12px there. The Input's own step is
               13.6px under 71rem, so the full-size field is lifted the same way. */
            compact ? 'paragraph h-10 max-lg:text-base lg:h-10' : 'max-lg:text-base',
            TONE[tone].input,
          )}
        />
        {/* compact pins the phone step too, or the recipe's max-lg: takes the
            button to 44px there while the field stays 40. */}
        <Button
          type='submit'
          tone={TONE[tone].button}
          busy={status === 'sending'}
          float={false}
          className={cn(
            'gap-2.5 rounded-l-none pr-5 max-lg:pr-4',
            compact && 'h-10 pr-4 pl-5 text-base max-lg:h-10 max-lg:pr-4 max-lg:pl-5 max-lg:text-base',
          )}>
          {status === 'sending' ? 'Signing up' : 'Sign up'}
          <ArrowIcon className='size-[1.1em]' />
        </Button>
      </div>

      {/* aria-live: the form does not navigate, so without it the outcome is
          announced to nobody. Always in the DOM, even unreserved: a live region
          that is display:none when its text arrives is announced to nobody
          either, so an empty one gives back the row gap rather than hiding. */}
      <p
        aria-live='polite'
        className={cn(
          'paragraph m-0',
          compact ? 'empty:-mt-2' : 'min-h-5',
          status === 'error' && 'text-scarlet-700',
          /* Periwinkle's dark step: the base step is 3.7 to one on white,
             under the 4.5 small text needs. */
          status === 'sent' && 'label pt-1 pl-1 text-periwinkle-700',
          status !== 'error' && status !== 'sent' && 'text-slate-600',
        )}>
        {message}
      </p>
    </form>
  );
};
